import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import CinematicBackground from "@/components/CinematicBackground";
import { PageHeader } from "@/pages/SceneAnalysis";
import ImagePicker from "@/components/ImagePicker";
import { ScoreRing } from "@/components/ScoreCard";
import { simulateExerciseScore } from "@/data/cinema";
import { useApp } from "@/context/AppContext";
import { useLang, useCinema } from "@/i18n/LanguageContext";

export default function Training() {
  const [openId, setOpenId] = useState(null);
  const { t, lang } = useLang();
  const { EXERCISES } = useCinema();
  return (
    <div className="relative px-4 pt-7" dir={lang === "ar" ? "rtl" : "ltr"} data-testid="training-page">
      <CinematicBackground />
      <PageHeader title={t("training_title")} icon="fa-dumbbell" />
      <p className="text-xs text-slate-400 mb-5">{t("training_intro")}</p>
      <div className="space-y-3">
        {EXERCISES.map((ex, i) => (
          <ExerciseCard key={ex.id} ex={ex} index={i} open={openId === ex.id} onToggle={() => setOpenId(openId === ex.id ? null : ex.id)} t={t} lang={lang} />
        ))}
      </div>
    </div>
  );
}

function ExerciseCard({ ex, index, open, onToggle, t, lang }) {
  const navigate = useNavigate();
  const { setSharedImage } = useApp();
  const [image, setImage] = useState(null);
  const [checked, setChecked] = useState({});
  const [result, setResult] = useState(null);

  const toggleCheck = (idx) => setChecked((c) => ({ ...c, [idx]: !c[idx] }));
  const checkedCount = Object.values(checked).filter(Boolean).length;

  const evaluate = () => {
    const scores = simulateExerciseScore(checkedCount, ex.checklist.length);
    const passed = ex.checklist.filter((_, i) => checked[i]);
    const failed = ex.checklist.filter((_, i) => !checked[i]);
    const en = lang === "en";
    setResult({
      scores,
      notes: buildNotes(scores.overall, en),
      strengths: passed.length ? passed.slice(0, 3) : [en ? "Good start" : "محاولة جيدة للبدء"],
      weaknesses: failed.length ? failed.slice(0, 3) : [en ? "No clear weaknesses" : "لا توجد نقاط ضعف واضحة"],
      improvements: failed.length ? failed.slice(0, 3).map((f) => (en ? `Focus on improving: ${f}` : `ركّز على تحسين: ${f}`)) : [en ? "Keep this level and try more complex lighting" : "حافظ على هذا المستوى وجرّب إضاءة أعقد"],
    });
  };

  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
      className="glass rounded-3xl overflow-hidden" data-testid={`exercise-${ex.id}`}>
      <button onClick={onToggle} className="w-full p-4 flex items-center gap-3 text-start">
        <div className="w-11 h-11 rounded-2xl glass glass-blue flex items-center justify-center shrink-0">
          <i className={`fa-solid ${ex.icon} text-gold`} />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-slate-100">{ex.title}</h3>
          <p className="text-xs text-slate-400 line-clamp-1">{ex.goal}</p>
        </div>
        <i className={`fa-solid fa-chevron-${open ? "up" : "down"} text-slate-500 text-xs`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-4 pb-4 space-y-3">
              <Block icon="fa-bullseye" color="#4ea8ff" label={t("goal")} text={ex.goal} />
              <Block icon="fa-circle-info" color="#d9b46a" label={t("instructions")} text={ex.instructions} />

              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-xs text-gold mb-2 flex items-center gap-2"><i className="fa-solid fa-list-ol" /> {t("steps")}</p>
                <ol className="space-y-1.5">
                  {ex.steps.map((s, i) => (
                    <li key={i} className="text-xs text-slate-200 flex gap-2"><span className="text-gold font-bold">{i + 1}.</span>{s}</li>
                  ))}
                </ol>
              </div>

              {image && <img src={image} alt="ex" className="w-full h-36 object-cover rounded-xl" />}
              <div className="grid grid-cols-1 gap-2">
                <ImagePicker onImage={(img) => { setImage(img); setSharedImage(img); }} testIdPrefix={`ex-${ex.id}`} />
                <button data-testid={`ex-open-ar-${ex.id}`} onClick={() => navigate("/app/camera")} className="glass rounded-2xl py-2.5 text-sm text-slate-200 flex items-center justify-center gap-2">
                  <i className="fa-solid fa-vr-cardboard" /> {t("openARCamera")}
                </button>
              </div>

              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-xs text-ai mb-2 flex items-center gap-2"><i className="fa-solid fa-clipboard-check" /> {t("checklist")}</p>
                <div className="space-y-1.5">
                  {ex.checklist.map((c, i) => (
                    <button key={i} data-testid={`check-${ex.id}-${i}`} onClick={() => toggleCheck(i)} className="w-full flex items-center gap-2 text-start">
                      <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${checked[i] ? "bg-gold border-gold" : "border-white/25"}`}>
                        {checked[i] && <i className="fa-solid fa-check text-[10px]" style={{ color: "#04060d" }} />}
                      </span>
                      <span className={`text-xs ${checked[i] ? "text-slate-100" : "text-slate-400"}`}>{c}</span>
                    </button>
                  ))}
                </div>
              </div>

              <button data-testid={`evaluate-${ex.id}`} onClick={evaluate} className="btn-gold w-full rounded-2xl py-3 font-display flex items-center justify-center gap-2">
                <i className="fa-solid fa-award" /> {t("evaluate")}
              </button>

              <AnimatePresence>
                {result && (
                  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="glass glass-blue rounded-2xl p-4 space-y-3" data-testid={`ex-result-${ex.id}`}>
                    <div className="flex items-center gap-4">
                      <ScoreRing value={result.scores.overall} size={90} color="#d9b46a" />
                      <div className="flex-1">
                        <p className="text-xs text-ai mb-1 flex items-center gap-1"><i className="fa-solid fa-robot" /> {t("aiNotes")}</p>
                        <p className="text-xs text-slate-200 leading-relaxed">{result.notes}</p>
                      </div>
                    </div>
                    <MiniList title={t("strengths")} color="#7bd88f" icon="fa-thumbs-up" items={result.strengths} />
                    <MiniList title={t("weaknesses")} color="#f0b93a" icon="fa-thumbs-down" items={result.weaknesses} />
                    <MiniList title={t("improvements")} color="#4ea8ff" icon="fa-arrow-trend-up" items={result.improvements} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

const Block = ({ icon, color, label, text }) => (
  <div className="flex items-start gap-2">
    <i className={`fa-solid ${icon} mt-0.5`} style={{ color }} />
    <div><p className="text-[11px]" style={{ color }}>{label}</p><p className="text-sm text-slate-200 leading-relaxed">{text}</p></div>
  </div>
);

const MiniList = ({ title, color, icon, items }) => (
  <div>
    <p className="text-[11px] mb-1 flex items-center gap-1" style={{ color }}><i className={`fa-solid ${icon}`} /> {title}</p>
    <ul className="space-y-1">
      {items.map((it, i) => <li key={i} className="text-xs text-slate-300 flex gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />{it}</li>)}
    </ul>
  </div>
);

function buildNotes(score, en) {
  if (en) {
    if (score >= 90) return "Excellent work! Your composition and lighting are very professional with precise control of the visual elements.";
    if (score >= 78) return "Very good. The composition is cohesive with small opportunities to improve lighting and balance.";
    if (score >= 65) return "Good. Review the unchecked points to raise the level of composition and lighting.";
    return "A promising start. Focus on the unmet checklist items to noticeably improve the score.";
  }
  if (score >= 90) return "أداء ممتاز! تكوينك وإضاءتك احترافيان جدًا مع تحكم دقيق في العناصر البصرية.";
  if (score >= 78) return "أداء جيد جدًا. التكوين متماسك مع فرص بسيطة لتحسين الإضاءة والتوازن.";
  if (score >= 65) return "أداء جيد. راجع النقاط غير المكتملة لرفع مستوى التكوين والإضاءة.";
  return "بداية واعدة. ركّز على عناصر قائمة التقييم غير المحققة لتحسين النتيجة بشكل ملحوظ.";
}
